import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../integrations/supabase/client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { LockKeyhole, Shield } from 'lucide-react';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [validResetLink, setValidResetLink] = useState(true);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Check if the user is authenticated with a recovery token
  useEffect(() => {
    const checkRecoveryToken = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      // If no session or no recovery token in URL, redirect to forgot password
      if (!session && !window.location.hash.includes('type=recovery')) {
        setValidResetLink(false);
        toast.error('Invalid or expired reset link. Please request a new one.');
      }
    };

    checkRecoveryToken();
  }, [navigate]);

  const validatePassword = (password: string) => {
    const errors: {[key: string]: string} = {};

    if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    } else if (!/[A-Z]/.test(password)) {
      errors.password = 'Password must contain at least one uppercase letter';
    } else if (!/[a-z]/.test(password)) {
      errors.password = 'Password must contain at least one lowercase letter';
    } else if (!/[0-9]/.test(password)) {
      errors.password = 'Password must contain at least one number';
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const passwordErrors = validatePassword(password);
    const newErrors: {[key: string]: string} = { ...passwordErrors };

    if (!password) {
      newErrors.password = 'Password is required';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      setResetComplete(true);
      toast.success('Password has been reset successfully.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password.');
      console.error('Password reset error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!validResetLink) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF7F0] px-4 py-12">
        <Card className="w-full max-w-md shadow-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-brand-secondary">Invalid Reset Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center py-6">
            <Shield className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="mb-4 text-brand-secondary/80">
              Please request a new password reset link.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button 
              onClick={() => navigate('/forgot-password')}
              className="bg-[#967F81] hover:bg-[#E33D16] text-white"
            >
              Request New Link
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF7F0] px-4 py-12">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-brand-secondary flex items-center">
            <LockKeyhole className="h-5 w-5 mr-2 text-brand-primary" />
            Reset Your Password
          </CardTitle>
          <CardDescription>
            {!resetComplete 
              ? 'Enter your new password below.'
              : 'Your password has been reset successfully.'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {!resetComplete ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  New Password
                </label>
                <Input 
                  id="password"
                  type="password" 
                  placeholder="••••••••" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={errors.password ? 'border-red-500' : ''}
                />
                <p className="text-xs text-gray-500">
                        At least 8 characters with uppercase, lowercase, and numbers.
                </p>
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password}</p>
                  )}
              </div>
                
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <Input 
                  id="confirmPassword"
                  type="password" 
                  placeholder="••••••••" 
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
              </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#967F81] hover:bg-[#E33D16] text-white"
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
          ) : (
            <div className="text-center py-6">
              <div className="bg-green-50 p-4 rounded-md border border-green-200 mb-4">
                <p className="text-green-700">
                  Your password has been reset successfully!
                </p>
              </div>
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-[#967F81] hover:bg-[#E33D16] text-white"
              >
                Sign In With New Password
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword; 